/**
 * ManoCV (My CV) Component
 * 
 * CV and cover letter management page for employees.
 * Allows users to view, create, edit, delete, and set primary CVs and cover letters.
 * 
 * @route /mano-cv
 * @access Employees only
 * 
 * Features:
 * - List of user's CVs with preview cards
 * - Primary (Publikuojama) CV selection (radio buttons)
 *   - Only one CV can be primary at a time
 *   - Primary CV is auto-selected for job applications
 * - Cover letter management
 *   - Primary cover letter selection
 *   - Used as default in job applications
 * - CV actions:
 *   - Create new CV
 *   - Edit existing CV
 *   - Delete CV
 *   - Preview CV
 *   - Print CV
 *   - Download CV
 *   - Email CV
 * 
 * Note:
 * - List order is preserved (creation date DESC)
 * - Primary selection doesn't reorder the list
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Settings, User, ArrowRight, Copy, Printer, Search } from 'lucide-react';
import './ManoCV.scss';

interface CV {
  id: number;
  title: string;
  is_primary: boolean;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  cv_language?: string;
  status?: 'draft' | 'pending' | 'published' | 'archived';
  boost_expires_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface CoverLetter {
  id: number;
  title: string;
  content: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

const ManoCV: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'cv' | 'cover-letters'>('cv');
  const [cvs, setCvs] = useState<CV[]>([]);
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  const [editingCoverLetter, setEditingCoverLetter] = useState<CoverLetter | null>(null);
  const [coverLetterForm, setCoverLetterForm] = useState({ title: '', content: '', is_primary: false });
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [selectedCVs, setSelectedCVs] = useState<number[]>([]);
  const [selectedLetters, setSelectedLetters] = useState<number[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchCVs();
      fetchCoverLetters();
    }
  }, [isAuthenticated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-wrapper')) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  const fetchCVs = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('CV API Response:', result);
        // Ensure we always set an array
        if (Array.isArray(result.data)) {
          setCvs(result.data);
        } else if (Array.isArray(result)) {
          setCvs(result);
        } else {
          console.warn('Unexpected CV response format:', result);
          setCvs([]);
        }
      } else {
        console.error('Failed to fetch CVs:', response.status);
        setCvs([]);
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
      setCvs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoverLetters = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cover-letters/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Cover Letters API Response:', result);
        // Ensure we always set an array
        if (Array.isArray(result.data)) {
          setCoverLetters(result.data);
        } else if (Array.isArray(result)) {
          setCoverLetters(result);
        } else {
          console.warn('Unexpected cover letters response format:', result);
          setCoverLetters([]);
        }
      } else {
        console.error('Failed to fetch cover letters:', response.status);
        setCoverLetters([]);
      }
    } catch (error) {
      console.error('Error fetching cover letters:', error);
      setCoverLetters([]);
    }
  };

  const handleDeleteCV = async (cvId: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šį CV?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/${cvId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCVs();
      } else {
        showToast('Nepavyko ištrinti CV', 'error');
      }
    } catch (error) {
      console.error('Error deleting CV:', error);
      showToast('Įvyko klaida trinant CV', 'error');
    }
  };

  const handleCopyCV = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Fetch the CV to copy
      const response = await fetch(`${API_URL}/cv/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const cvToCopy = result.data;
        
        // Create a copy with modified title
        const copyData = {
          ...cvToCopy,
          title: `${cvToCopy.title} (Kopija)`,
          is_primary: false, // Copies are not primary by default
          // Remove ID and timestamps so a new one is created
          id: undefined,
          created_at: undefined,
          updated_at: undefined
        };

        // Create the new CV
        const createResponse = await fetch(`${API_URL}/cv`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(copyData)
        });

        if (createResponse.ok) {
          showToast('CV sėkmingai nukopijuotas!', 'success');
          fetchCVs(); // Refresh the list
        } else {
          showToast('Nepavyko nukopijuoti CV', 'error');
        }
      }
    } catch (error) {
      console.error('Error copying CV:', error);
      showToast('Įvyko klaida kopijuojant CV', 'error');
    }
    setOpenDropdownId(null);
  };

  const handlePrintCV = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Fetch the CV data
      const response = await fetch(`${API_URL}/cv/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const cv = result.data;
        
        // Open CV in new window for printing
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(generatePrintableCV(cv));
          printWindow.document.close();
          printWindow.focus();
          
          // Wait for content to load, then print
          setTimeout(() => {
            printWindow.print();
          }, 500);
        }
      } else {
        showToast('Nepavyko gauti CV duomenų', 'error');
      }
    } catch (error) {
      console.error('Error printing CV:', error);
      showToast('Įvyko klaida spausdinant CV', 'error');
    }
    setOpenDropdownId(null);
  };

  const generatePrintableCV = (cv: any): string => {
    const imageUrl = cv.profile_image_url ? `http://localhost:5000${cv.profile_image_url}` : '';
    const formatDate = (dateStr: string) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('lt-LT');
    };
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${cv.title}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body {
            font-family: 'Inter', 'Segoe UI', sans-serif;
            line-height: 1.6;
            color: #1a1a1a;
            background: white;
            font-size: 11pt;
          }
          
          .cv-container {
            max-width: 210mm;
            margin: 0 auto;
            background: white;
            position: relative;
          }
          
          /* Two-column layout */
          .cv-layout {
            display: flex;
            min-height: 297mm;
          }
          
          /* Left sidebar - colored */
          .cv-sidebar {
            width: 35%;
            background: linear-gradient(135deg, #0AA271 0%, #23928B 100%);
            color: white;
            padding: 40px 30px;
          }
          
          .cv-photo-wrapper {
            text-align: center;
            margin-bottom: 30px;
          }
          
          .cv-photo {
            width: 140px;
            height: 140px;
            border-radius: 50%;
            object-fit: cover;
            border: 5px solid rgba(255, 255, 255, 0.3);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          }
          
          .cv-name {
            font-size: 24pt;
            font-weight: 700;
            margin-bottom: 8px;
            text-align: center;
            line-height: 1.2;
          }
          
          .cv-title-job {
            font-size: 12pt;
            font-weight: 300;
            text-align: center;
            opacity: 0.9;
            margin-bottom: 30px;
          }
          
          .sidebar-section {
            margin-bottom: 30px;
          }
          
          .sidebar-section h3 {
            font-size: 11pt;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid rgba(255, 255, 255, 0.3);
          }
          
          .contact-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 12px;
            font-size: 10pt;
            line-height: 1.4;
          }
          
          .contact-icon {
            width: 20px;
            flex-shrink: 0;
            opacity: 0.9;
          }
          
          .skill-item {
            background: rgba(255, 255, 255, 0.2);
            padding: 6px 12px;
            border-radius: 15px;
            display: inline-block;
            margin: 4px 4px 4px 0;
            font-size: 9pt;
            font-weight: 500;
          }
          
          .language-item {
            margin-bottom: 10px;
          }
          
          .language-name {
            font-weight: 600;
            font-size: 10pt;
          }
          
          .language-level {
            font-size: 9pt;
            opacity: 0.9;
          }
          
          /* Right main content */
          .cv-main {
            width: 65%;
            padding: 40px;
          }
          
          .main-section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          
          .main-section h2 {
            font-size: 14pt;
            font-weight: 700;
            color: #23928B;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 3px solid #23928B;
          }
          
          .about-text {
            font-size: 10pt;
            line-height: 1.7;
            color: #4a4a4a;
            text-align: justify;
          }
          
          .experience-item, .education-item {
            margin-bottom: 25px;
            position: relative;
            padding-left: 20px;
            border-left: 2px solid #e0e0e0;
          }
          
          .experience-item:before {
            content: '';
            position: absolute;
            left: -6px;
            top: 5px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #23928B;
          }
          
          .item-title {
            font-size: 12pt;
            font-weight: 600;
            color: #1a1a1a;
            margin-bottom: 4px;
          }
          
          .item-company {
            font-size: 10pt;
            font-weight: 500;
            color: #23928B;
            margin-bottom: 4px;
          }
          
          .item-period {
            font-size: 9pt;
            color: #888;
            margin-bottom: 8px;
          }
          
          .item-description {
            font-size: 10pt;
            line-height: 1.6;
            color: #4a4a4a;
          }
          
          .skills-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 8px;
          }
          
          .skill-badge {
            background: #f0f0f0;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 9pt;
            text-align: center;
            font-weight: 500;
            color: #333;
          }
          
          @media print {
            body { 
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            .cv-layout {
              page-break-inside: avoid;
            }
            .cv-sidebar {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="cv-container">
          <div class="cv-layout">
            <!-- LEFT SIDEBAR -->
            <div class="cv-sidebar">
              ${imageUrl ? `
                <div class="cv-photo-wrapper">
                  <img src="${imageUrl}" alt="Profile" class="cv-photo" />
                </div>
              ` : ''}
              
              <div class="cv-name">${cv.first_name || ''} ${cv.last_name || ''}</div>
              ${cv.job_preferences?.position ? `
                <div class="cv-title-job">${cv.job_preferences.position}</div>
              ` : ''}
              
              <!-- Contact Information -->
              <div class="sidebar-section">
                <h3>Kontaktai</h3>
                ${cv.email ? `
                  <div class="contact-item">
                    <span class="contact-icon">📧</span>
                    <span>${cv.email}</span>
                  </div>
                ` : ''}
                ${cv.phone ? `
                  <div class="contact-item">
                    <span class="contact-icon">📱</span>
                    <span>${cv.phone}</span>
                  </div>
                ` : ''}
                ${cv.address ? `
                  <div class="contact-item">
                    <span class="contact-icon">📍</span>
                    <span>${cv.address}</span>
                  </div>
                ` : ''}
                ${cv.birth_date ? `
                  <div class="contact-item">
                    <span class="contact-icon">📅</span>
                    <span>${formatDate(cv.birth_date)}</span>
                  </div>
                ` : ''}
              </div>
              
              <!-- Languages -->
              ${cv.language_skills && cv.language_skills.length > 0 ? `
                <div class="sidebar-section">
                  <h3>Kalbos</h3>
                  ${cv.language_skills.map((lang: any) => `
                    <div class="language-item">
                      <div class="language-name">${lang.language || lang.name}</div>
                      <div class="language-level">${lang.level || ''}</div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
              
              <!-- Driving Skills -->
              ${cv.driving_skills && cv.driving_skills.length > 0 ? `
                <div class="sidebar-section">
                  <h3>Vairavimas</h3>
                  <div>
                    ${cv.driving_skills.map((cat: any) => `
                      <span class="skill-item">${cat.category || cat}</span>
                    `).join('')}
                  </div>
                </div>
              ` : ''}
              
              <!-- Computer Skills -->
              ${cv.computer_skills && Object.keys(cv.computer_skills).some((key: string) => cv.computer_skills[key].length > 0) ? `
                <div class="sidebar-section">
                  <h3>IT Įgūdžiai</h3>
                  ${Object.entries(cv.computer_skills).map(([category, skills]: [string, any]) => 
                    skills.length > 0 ? `
                      ${skills.map((skill: string) => `<span class="skill-item">${skill}</span>`).join('')}
                    ` : ''
                  ).join('')}
                </div>
              ` : ''}
            </div>
            
            <!-- RIGHT MAIN CONTENT -->
            <div class="cv-main">
              <!-- About Me -->
              ${cv.description ? `
                <div class="main-section">
                  <h2>Apie mane</h2>
                  <p class="about-text">${cv.description}</p>
                </div>
              ` : ''}
              
              <!-- Work Experience -->
              ${cv.work_experience && cv.work_experience.length > 0 ? `
                <div class="main-section">
                  <h2>Darbo patirtis</h2>
                  ${cv.work_experience.map((exp: any) => `
                    <div class="experience-item">
                      <div class="item-title">${exp.position || 'Pozicija'}</div>
                      <div class="item-company">${exp.company || 'Įmonė'}</div>
                      <div class="item-period">${exp.period || exp.startDate || ''}</div>
                      ${exp.description ? `<div class="item-description">${exp.description}</div>` : ''}
                    </div>
                  `).join('')}
                </div>
              ` : ''}
              
              <!-- Education -->
              ${cv.education && cv.education.length > 0 ? `
                <div class="main-section">
                  <h2>Išsilavinimas</h2>
                  ${cv.education.map((edu: any) => `
                    <div class="experience-item">
                      <div class="item-title">${edu.degree || 'Kvalifikacija'}</div>
                      <div class="item-company">${edu.institution || 'Institucija'}</div>
                      <div class="item-period">${edu.period || edu.year || ''}</div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
              
              <!-- Additional Training -->
              ${cv.additional_training && cv.additional_training.length > 0 ? `
                <div class="main-section">
                  <h2>Papildomi mokymai</h2>
                  ${cv.additional_training.map((training: any) => `
                    <div class="experience-item">
                      <div class="item-title">${training.course || training.title || 'Mokymai'}</div>
                      <div class="item-company">${training.institution || training.organizer || ''}</div>
                      <div class="item-period">${training.year || training.period || ''}</div>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
              
              <!-- Personal Qualities -->
              ${cv.personal_qualities ? `
                <div class="main-section">
                  <h2>Asmeninės savybės</h2>
                  <p class="about-text">${cv.personal_qualities}</p>
                </div>
              ` : ''}
              
              <!-- Hobbies -->
              ${cv.hobbies ? `
                <div class="main-section">
                  <h2>Pomėgiai</h2>
                  <p class="about-text">${cv.hobbies}</p>
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const toggleDropdown = (id: number) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleCreateCoverLetter = () => {
    setEditingCoverLetter(null);
    setCoverLetterForm({ title: '', content: '', is_primary: false });
    setShowCoverLetterModal(true);
  };

  const handleEditCoverLetter = (coverLetter: CoverLetter) => {
    setEditingCoverLetter(coverLetter);
    setCoverLetterForm({
      title: coverLetter.title,
      content: coverLetter.content,
      is_primary: coverLetter.is_primary
    });
    setShowCoverLetterModal(true);
  };

  const handleSaveCoverLetter = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const url = editingCoverLetter
        ? `${API_URL}/cover-letters/${editingCoverLetter.id}`
        : `${API_URL}/cover-letters`;
      
      const method = editingCoverLetter ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(coverLetterForm)
      });

      if (response.ok) {
        fetchCoverLetters();
        setShowCoverLetterModal(false);
        setCoverLetterForm({ title: '', content: '', is_primary: false });
      } else {
        showToast('Nepavyko išsaugoti motyvacinio laiško', 'error');
      }
    } catch (error) {
      console.error('Error saving cover letter:', error);
      showToast('Įvyko klaida saugant motyvacinio laiško', 'error');
    }
  };

  const handleDeleteCoverLetter = async (id: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šį motyvacinį laišką?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cover-letters/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCoverLetters();
      } else {
        showToast('Nepavyko ištrinti motyvacinio laiško', 'error');
      }
    } catch (error) {
      console.error('Error deleting cover letter:', error);
      showToast('Įvyko klaida trinant motyvacinį laišką', 'error');
    }
  };

  const handleSetPrimaryCV = async (cvId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv/${cvId}/set-primary`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCVs(); // Refresh the list to show updated primary status
      } else {
        showToast('Nepavyko nustatyti kaip pagrindinio CV', 'error');
      }
    } catch (error) {
      console.error('Error setting primary CV:', error);
      showToast('Įvyko klaida nustatant pagrindinį CV', 'error');
    }
  };

  const handleSetPrimaryCoverLetter = async (letterId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cover-letters/${letterId}/set-primary`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchCoverLetters(); // Refresh the list to show updated primary status
      } else {
        showToast('Nepavyko nustatyti kaip pagrindinio laiško', 'error');
      }
    } catch (error) {
      console.error('Error setting primary cover letter:', error);
      showToast('Įvyko klaida nustatant pagrindinį laišką', 'error');
    }
  };

  // Filter functions
  const filterCVsByTitle = (items: CV[]) => {
    if (!searchTitleFilter.trim()) return items;
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return items.filter(cv => cv.title.toLowerCase().includes(filterLower));
  };

  const filterLettersByTitle = (items: CoverLetter[]) => {
    if (!searchTitleFilter.trim()) return items;
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return items.filter(letter => letter.title.toLowerCase().includes(filterLower));
  };

  // CV selection handlers
  const handleToggleSelectAllCVs = () => {
    const filteredCVs = filterCVsByTitle(cvs);
    if (selectedCVs.length === filteredCVs.length && filteredCVs.length > 0) {
      setSelectedCVs([]);
    } else {
      setSelectedCVs(filteredCVs.map(cv => cv.id));
    }
  };

  const handleToggleSelectCV = (id: number) => {
    setSelectedCVs(prev => 
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelectedCVs = async () => {
    if (selectedCVs.length === 0) return;
    if (!window.confirm(`Ar tikrai norite ištrinti ${selectedCVs.length} CV?`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await Promise.all(
        selectedCVs.map(id =>
          fetch(`${API_URL}/cv/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      setSelectedCVs([]);
      fetchCVs();
    } catch (error) {
      console.error('Error deleting CVs:', error);
      showToast('Įvyko klaida trinant CV', 'error');
    }
  };

  // Cover letter selection handlers
  const handleToggleSelectAllLetters = () => {
    const filteredLetters = filterLettersByTitle(coverLetters);
    if (selectedLetters.length === filteredLetters.length && filteredLetters.length > 0) {
      setSelectedLetters([]);
    } else {
      setSelectedLetters(filteredLetters.map(l => l.id));
    }
  };

  const handleToggleSelectLetter = (id: number) => {
    setSelectedLetters(prev => 
      prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelectedLetters = async () => {
    if (selectedLetters.length === 0) return;
    if (!window.confirm(`Ar tikrai norite ištrinti ${selectedLetters.length} motyvacinius laiškus?`)) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await Promise.all(
        selectedLetters.map(id =>
          fetch(`${API_URL}/cover-letters/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      setSelectedLetters([]);
      fetchCoverLetters();
    } catch (error) {
      console.error('Error deleting cover letters:', error);
      showToast('Įvyko klaida trinant motyvacinius laiškus', 'error');
    }
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="mano-cv-page">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  if (!isAuthenticated || (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker')) {
    return <Navigate to="/" replace />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const filteredCVs = filterCVsByTitle(cvs);
  const filteredLetters = filterLettersByTitle(coverLetters);

  return (
    <div className="mano-cv-page">
      <div className="container">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <span onClick={() => navigate('/')}>Pradžia</span>
          <span className="separator">&gt;</span>
          <span className="current">Mano CV</span>
        </div>

        {/* Tabs row */}
        <div className="tabs-row">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'cv' ? 'active' : ''}`}
              onClick={() => { setActiveTab('cv'); setSearchTitleFilter(''); }}
            >
              Mano CV
              {cvs.length > 0 && <span className="count">{cvs.length}</span>}
            </button>
            <button
              className={`tab ${activeTab === 'cover-letters' ? 'active' : ''}`}
              onClick={() => { setActiveTab('cover-letters'); setSearchTitleFilter(''); }}
            >
              Motyvaciniai laiškai
              {coverLetters.length > 0 && <span className="count">{coverLetters.length}</span>}
            </button>
          </div>
          
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={activeTab === 'cv' ? handleToggleSelectAllCVs : handleToggleSelectAllLetters}
              >
                <input 
                  type="checkbox" 
                  checked={activeTab === 'cv' 
                    ? (selectedCVs.length > 0 && selectedCVs.length === filteredCVs.length)
                    : (selectedLetters.length > 0 && selectedLetters.length === filteredLetters.length)
                  }
                  onChange={activeTab === 'cv' ? handleToggleSelectAllCVs : handleToggleSelectAllLetters}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus</span>
              </button>
            </div>
            <button 
              className="btn-delete"
              onClick={activeTab === 'cv' ? handleDeleteSelectedCVs : handleDeleteSelectedLetters}
              disabled={activeTab === 'cv' ? selectedCVs.length === 0 : selectedLetters.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder={activeTab === 'cv' ? 'CV pavadinimas' : 'Laiško pavadinimas'}
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
            <button 
              className="btn-new-search" 
              onClick={activeTab === 'cv' ? () => navigate('/cv/create') : handleCreateCoverLetter}
            >
              <Plus size={16} />
              <span>{activeTab === 'cv' ? 'Naujas CV' : 'Naujas laiškas'}</span>
            </button>
          </div>
        </div>

        {activeTab === 'cv' && (
          <div className="cv-section">

            {loading ? (
              <div className="loading">Kraunama...</div>
            ) : filteredCVs.length === 0 ? (
              <div className="empty-state">
                <p>{searchTitleFilter ? 'CV pagal šį filtrą nerasta' : 'Dar neturite sukurtų CV'}</p>
                {!searchTitleFilter && (
                  <button className="btn-primary" onClick={() => navigate('/cv/create')}>
                    Sukurti pirmą CV
                  </button>
                )}
              </div>
            ) : (
              <div className="cv-list">
                {filteredCVs.map((cv) => (
                  <div key={cv.id} className={`cv-card ${selectedCVs.includes(cv.id) ? 'selected' : ''}`}>
                    <div className="cv-card-header">
                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={selectedCVs.includes(cv.id)}
                          onChange={() => handleToggleSelectCV(cv.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="cv-status">
                        {cv.status === 'pending' ? (
                          <span className="cv-pending-badge">Laukia patvirtinimo</span>
                        ) : (
                          <>
                            <input 
                              type="radio" 
                              name="primary-cv" 
                              checked={cv.is_primary}
                              onChange={() => handleSetPrimaryCV(cv.id)}
                            />
                            <User size={16} />
                            <span className="status-label">
                              {cv.is_primary ? 'Publikuojama' : 'Publikuoti'}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="cv-header-actions">
                        <div className="dropdown-wrapper">
                          <button
                            className="btn-icon-small"
                            onClick={() => toggleDropdown(cv.id)}
                            title="Nustatymai"
                          >
                            <Settings size={18} />
                          </button>
                          {openDropdownId === cv.id && (
                            <div className="dropdown-menu">
                              <button onClick={() => { navigate(`/cv/edit/${cv.id}`); setOpenDropdownId(null); }}>
                                <Edit size={16} />
                                Redaguoti
                              </button>
                              <button onClick={() => handleCopyCV(cv.id)}>
                                <Copy size={16} />
                                Kopijuoti
                              </button>
                              <button onClick={() => handlePrintCV(cv.id)}>
                                <Printer size={16} />
                                Spausdinti
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          className="btn-icon-small"
                          onClick={() => handleDeleteCV(cv.id)}
                          title="Ištrinti"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="cv-card-body">
                      <h3 className="cv-title">{cv.title}</h3>
                      <p className="cv-date">Atnaujinta: {formatDate(cv.updated_at)}</p>
                    </div>

                    <div className="cv-card-posting">
                      <div className="posting-info">
                        {cv.boost_expires_at && new Date(cv.boost_expires_at) > new Date() ? (
                          <>
                            <span className="posting-label">CV iškeltas iki:</span>
                            <span className="posting-date">{formatDate(cv.boost_expires_at)}</span>
                          </>
                        ) : (
                          <span className="posting-label">CV {cv.is_primary ? 'neiškeltas' : 'neiškeltas'}</span>
                        )}
                      </div>
                      <button 
                        className={`btn-extend${!cv.is_primary ? ' disabled' : ''}`}
                        onClick={() => navigate('/employee/services')}
                        disabled={!cv.is_primary}
                        title={!cv.is_primary ? 'Publikuokite CV, kad galėtumėte užsakyti iškėlimą' : ''}
                      >
                        {cv.boost_expires_at && new Date(cv.boost_expires_at) > new Date() 
                          ? 'PRATĘSTI CV IŠKĖLIMĄ' 
                          : 'UŽSAKYTI CV IŠKĖLIMĄ'}
                      </button>
                    </div>

                    <div className="cv-card-languages">
                      <span className="languages-label">Aktyvuotos kalbos:</span>
                      <div className="language-badges">
                        <span className={`language-badge ${!cv.cv_language || cv.cv_language === 'Lietuvių' ? 'active' : ''}`}>LT</span>
                        <span className={`language-badge ${cv.cv_language === 'English' ? 'active' : ''}`}>EN</span>
                      </div>
                    </div>

                    <button className="cv-card-preview" onClick={() => navigate(`/cv/edit/${cv.id}`)}>
                      PERŽIŪRĖTI
                      <ArrowRight size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'cover-letters' && (
          <div className="cover-letters-section">
            {filteredLetters.length === 0 ? (
              <div className="empty-state">
                <p>{searchTitleFilter ? 'Laiškų pagal šį filtrą nerasta' : 'Dar neturite sukurtų motyvacinių laiškų'}</p>
                {!searchTitleFilter && (
                  <button className="btn-primary" onClick={handleCreateCoverLetter}>
                    Sukurti pirmą laišką
                  </button>
                )}
              </div>
            ) : (
              <div className="cover-letter-list">
                {filteredLetters.map((letter) => (
                  <div key={letter.id} className={`cover-letter-card ${selectedLetters.includes(letter.id) ? 'selected' : ''}`}>
                    <div className="letter-card-header">
                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={selectedLetters.includes(letter.id)}
                          onChange={() => handleToggleSelectLetter(letter.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="letter-status">
                        <input 
                          type="radio" 
                          name="primary-letter" 
                          checked={letter.is_primary}
                          onChange={() => handleSetPrimaryCoverLetter(letter.id)}
                        />
                        <span className="status-label">
                          {letter.is_primary ? 'Pagrindinis' : 'Papildomas'}
                        </span>
                      </div>
                      <div className="letter-header-actions">
                        <button
                          className="btn-icon-small"
                          onClick={() => handleEditCoverLetter(letter)}
                          title="Redaguoti"
                        >
                          <Settings size={18} />
                        </button>
                        <button
                          className="btn-icon-small"
                          onClick={() => handleDeleteCoverLetter(letter.id)}
                          title="Ištrinti"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="letter-card-body">
                      <h3 className="letter-title">{letter.title}</h3>
                      <p className="letter-preview">
                        {letter.content.substring(0, 200)}
                        {letter.content.length > 200 ? '...' : ''}
                      </p>
                      <p className="letter-date">Atnaujinta: {formatDate(letter.updated_at)}</p>
                    </div>

                    <button className="letter-card-preview" onClick={() => handleEditCoverLetter(letter)}>
                      PERŽIŪRĖTI / REDAGUOTI
                      <ArrowRight size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cover Letter Modal */}
        {showCoverLetterModal && (
          <div className="modal-overlay" onClick={() => setShowCoverLetterModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingCoverLetter ? 'Redaguoti motyvacinį laišką' : 'Naujas motyvacinis laiškas'}</h2>
                <button className="btn-close" onClick={() => setShowCoverLetterModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Pavadinimas *</label>
                  <input
                    type="text"
                    value={coverLetterForm.title}
                    onChange={(e) => setCoverLetterForm({ ...coverLetterForm, title: e.target.value })}
                    placeholder="Pvz., Motyvacinis laiškas IT pozicijai"
                  />
                </div>
                <div className="form-group">
                  <label>Tekstas *</label>
                  <textarea
                    value={coverLetterForm.content}
                    onChange={(e) => setCoverLetterForm({ ...coverLetterForm, content: e.target.value })}
                    placeholder="Įveskite motyvacinio laiško tekstą..."
                    rows={12}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={coverLetterForm.is_primary}
                      onChange={(e) => setCoverLetterForm({ ...coverLetterForm, is_primary: e.target.checked })}
                    />
                    Nustatyti kaip pagrindinį
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowCoverLetterModal(false)}>
                  Atšaukti
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSaveCoverLetter}
                  disabled={!coverLetterForm.title || !coverLetterForm.content}
                >
                  {editingCoverLetter ? 'Išsaugoti' : 'Sukurti'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManoCV;

